import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  Columns,
  Briefcase,
  CalendarClock,
  Megaphone,
  UserCheck,
  Settings as SettingsIcon,
  LogOut,
  Sun,
  Moon,
  Search,
  FileSignature,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import { useTheme } from "./ThemeContext";
import SettingsPanel from "./SettingsPanel";

const items = [
  { to: "/crm", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/crm/contacts", label: "Contacts", icon: Users },
  { to: "/crm/sign", label: "Sign", icon: FileSignature },
  { to: "/crm/followups", label: "Follow-ups", icon: CalendarClock },
  { to: "/crm/pipeline", label: "Pipeline", icon: Columns },
  { to: "/crm/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/crm/cases", label: "Cases", icon: Briefcase },
];

export default function CrmLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const nav = useNavigate();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div
      className="h-screen w-full flex overflow-hidden"
      style={{
        background: "var(--crm-bg)",
        color: "var(--crm-text)",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Inter", system-ui, sans-serif',
      }}
      data-testid="crm-shell"
      data-theme={theme}
    >
      {/* Sidebar */}
      <aside
        className="w-60 shrink-0 flex flex-col"
        style={{
          background: "var(--crm-side-bg)",
          borderRight: "1px solid var(--crm-line)",
        }}
      >
        {/* Brand */}
        <div className="flex items-center gap-3 px-5 pt-6 pb-8">
          <img
            src="/prospera-icon.png"
            alt=""
            className="w-8 h-8 rounded-full"
            style={{ boxShadow: "0 0 0 1px rgba(183,138,47,0.35)" }}
          />
          <div className="leading-tight">
            <div className="text-[13px] font-semibold" style={{ color: "var(--crm-text)" }}>
              Prospera
            </div>
            <div className="text-[10.5px]" style={{ color: "var(--crm-text-muted)" }}>
              Advisor Console
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 space-y-0.5">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-[13.5px] transition ${
                  isActive ? "font-medium" : "font-normal"
                }`
              }
              style={({ isActive }) => ({
                background: isActive ? "var(--crm-accent-soft)" : "transparent",
                color: isActive ? "var(--crm-accent)" : "var(--crm-text-muted)",
              })}
            >
              <it.icon size={16} strokeWidth={1.75} />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User block */}
        <div className="px-3 pb-4">
          <div
            className="flex items-center gap-3 px-2 py-2 rounded-lg"
            data-testid="user-block"
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
              style={{
                background: "linear-gradient(180deg, #f4d47b 0%, #d4a53a 100%)",
                color: "#000",
              }}
            >
              {(user?.name?.[0] || user?.email?.[0] || "B").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] truncate" style={{ color: "var(--crm-text)" }}>
                {user?.name || user?.email?.split("@")[0] || "Advisor"}
              </div>
              <div className="text-[11px]" style={{ color: "var(--crm-text-subtle)" }}>
                Signed in
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header
          className="flex items-center gap-4 px-8 h-14 shrink-0"
          style={{
            background: "var(--crm-topbar-bg)",
            backdropFilter: "saturate(180%) blur(20px)",
            WebkitBackdropFilter: "saturate(180%) blur(20px)",
            borderBottom: "1px solid var(--crm-line-soft)",
          }}
        >
          {/* Search */}
          <div className="flex-1 max-w-md relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: "var(--crm-text-subtle)" }}
            />
            <input
              placeholder="Search clients, cases, notes…"
              data-testid="topbar-search"
              className="w-full pl-9 pr-3 py-1.5 rounded-lg text-[13px] focus:outline-none"
              style={{
                background: "var(--crm-hover)",
                color: "var(--crm-text)",
                border: "1px solid transparent",
              }}
            />
          </div>

          <div className="flex items-center gap-1">
            <IconBtn onClick={toggleTheme} testid="theme-toggle" aria-label="Toggle theme">
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </IconBtn>
            <IconBtn onClick={() => setSettingsOpen(true)} testid="settings-btn" aria-label="Settings">
              <SettingsIcon size={15} />
            </IconBtn>
            <IconBtn onClick={doLogout} testid="logout-btn" aria-label="Sign out">
              <LogOut size={15} />
            </IconBtn>
          </div>
        </header>

        {/* Main scroll area — Apple-smooth page transitions */}
        <main
          className="flex-1 overflow-y-auto"
          style={{ background: "var(--crm-bg)" }}
        >
          <div className="max-w-[1400px] mx-auto px-8 py-8">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 8, scale: 0.995 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.998 }}
                transition={{
                  duration: 0.32,
                  ease: [0.32, 0.72, 0, 1], // Apple's signature spring curve
                }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>

      <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}

function IconBtn({ children, onClick, testid, ...rest }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-8 h-8 rounded-lg flex items-center justify-center transition"
      style={{ color: "var(--crm-text-muted)" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--crm-hover)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      {...rest}
    >
      {children}
    </button>
  );
}
